import { Enquiry } from "../../components/Enquiry";
import { Footer } from "../../components/Footer";
import { Layout } from "../../components/Layout";
import LogoMarquee from "@/components/Sections/LogoMarquee";
import { CTA_LINK, CTA_TEXT, DEMO_TEXT } from "@/components/layout/PageLayout";
import { FeatureShowcase } from "@/components/layout/sections/FeatureShowcase";
import { Hero } from "@/components/layout/sections/Hero";
import { ProductFooter } from "@/components/layout/sections/ProductFooter";
import Section from "@/components/layout/sections/Section";
import TitleSection from "@/components/layout/text/TitleSection/TitleSection";
import { faBadgeCheck, faCircleInfo, faCirclePlay, faNewspaper, faScreenUsers, faStar, faTimer } from "@icons/regular";
import { Button, Icon } from "@ui";
import { graphql } from "gatsby";
import { Link } from "gatsby";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { Helmet } from "react-helmet";

const gridFeatures = [
  {
    id: 1,
    title: "Co-creation sessions",
    description: "Sit in with our experts to bring your design vision to life and gain the know-how to keep creating with confidence.",
    imageUrl: "/product/support/co-creating-sessions.png",
    imageAlt: "A screenshot of collaborative editing in the Shorthand editor",
  },
  {
    id: 2,
    title: "Design services",
    description:
      "Need design support or short on resources? Team up with our  experienced design partners to craft polished, high-quality Shorthand stories for your brand.",
    imageUrl: "/product/support/design-services.png",
    imageAlt: "A logo of Shorthand design services",
  },
  {
    id: 3,
    title: "Training",
    description: "Access hands-on training sessions tailored to onboard you and your team to Shorthand's features and best practices.",
    imageUrl: "/product/support/training.png",
    imageAlt: "A screenshot of an online training course using the Shorthand editor",
  },
  {
    id: 4,
    title: "Technical support",
    description: (
      <>
        Got a glitch? Our engineers move fast to fix technical issues, which is why our support is top-rated on G2.{" "}
        <a href="https://www.g2.com/products/shorthand/reviews" target="_blank" rel="noopener noreferrer">
          Read our reviews on G2
        </a>
        .
      </>
    ),
    imageUrl: "/product/support/tech-support.png",
    imageAlt: "A screenshot of G2 reviews for Shorthand",
  },
];

const freeResources = [
  {
    id: 1,
    icon: faScreenUsers,
    title: "Webinars",
    description: (
      <>
        Sign up for an upcoming free training session, or watch on demand. <Link to="/training">Learn more.</Link>
      </>
    ),
  },
  {
    id: 2,
    icon: faCirclePlay,
    title: "Shorthand 101",
    description: (
      <>
        Watch bite-sized video tutorials to help you master the platform quickly.{" "}
        <a href="https://www.youtube.com/playlist?list=PLoaXrNfyvvDZEsbEssAu3lYJJ4WJ21hUo" target="_blank" rel="noopener noreferrer">
          Learn more
        </a>
        .
      </>
    ),
  },
  {
    id: 3,
    icon: faCircleInfo,
    title: "Help centre",
    description: (
      <>
        Find clear answers on every Shorthand feature.{" "}
        <a href="https://support.shorthand.com/en" target="_blank" rel="noopener noreferrer">
          Learn more
        </a>
        .
      </>
    ),
  },
];

const FeaturesSupport: React.FC<{ location: any }> = props => {
  const [showPortal, setShowPortal] = React.useState(false);
  const prePopulatedFields = React.useRef<Record<any, string>>({});

  function handleShowPortal(fields?: Record<any, string>): void {
    setShowPortal(true);
    prePopulatedFields.current = fields || {};
  }

  const portalProps = {
    type: "general-enquiry",
    subject: "general-enquiry",
    prePopulatedFields: prePopulatedFields.current,
    onClose: () => {
      setShowPortal(false);
      prePopulatedFields.current = {};
    },
  };

  return (
    <Layout location={props.location} theme="dark-theme">
      {showPortal && ReactDOM.createPortal(<Enquiry {...portalProps} />, document.getElementById("modal"))}
      <main id="main" className="marketing-content container-fluid">
        <Helmet
          bodyAttributes={{
            class: "body-shorthand body-marketing body-marketing-secondary body-marketing-features body-light hide-elevio",
          }}
        ></Helmet>

        <Hero
          title={<>Industry-leading support </>}
          description={
            <>
              From onboarding to optimisation, our expert team is available to answer questions, troubleshoot technical issues, and
              offer design help — ensuring you always have the right guidance and get results.
            </>
          }
          actions={
            <>
              <Button asChild variant="primary-gradient" size="lg">
                <a href={CTA_LINK} className="tw-no-underline hover:tw-no-underline">
                  {CTA_TEXT}
                </a>
              </Button>
              <Button
                variant="secondary-outline"
                style={{ border: "1px solid white" }}
                size="lg"
                onClick={() =>
                  handleShowPortal({
                    interestedIn: "demo",
                    message: "booking a demo of the Shorthand platform",
                  })
                }
              >
                {DEMO_TEXT}
              </Button>
            </>
          }
        />

        <Section>
          <FeatureShowcase
            preventOverflow
            title={
              <>
                With you <span className="tw-font-PassengerSerif tw-italic">every step</span>
              </>
            }
            description={"We’re here to help your team succeed."}
            features={[
              {
                name: "Fast average response times — get the help you need, when you need it",
                icon: faTimer,
              },
              {
                name: "Top-rated on G2 and recognised by real users for outstanding service",
                icon: faStar,
              },
              {
                name: "Experienced, world-class support team and editorial feedback",
                icon: faNewspaper,
              },
              {
                name: "Trusted by leading brands with proven success across industries",
                icon: faBadgeCheck,
              },
            ]}
            image="product/support/support-hero.png"
            imagePlacement="left"
          />
          <LogoMarquee />
        </Section>
        <Section>
          <TitleSection
            title="Your partners in success"
            description="We partner with you to bring your stories to life, offering creative guidance, expert insights, and dedicated support every step of the way."
          />
          <div className="tw-mx-auto tw-mt-10 tw-grid tw-max-w-4xl tw-grid-cols-2 tw-gap-4 sm:tw-mt-16">
            {gridFeatures.map(feature => (
              <div className="tw-relative" key={`support-grid-feature-${feature.id}`}>
                <div className="tw-absolute tw-inset-px tw-rounded-lg tw-bg-grey_scale-800/50" />
                <div className="tw-relative tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-[calc(theme(borderRadius.lg)+1px)]">
                  <div className="tw-px-4 tw-pb-1 tw-pt-4 sm:tw-px-10 sm:tw-pb-0 sm:tw-pt-10">
                    <p className="tw-mt-2 !tw-text-lg tw-font-medium tw-tracking-tight tw-text-[var(--foreground-color)] default-max-lg:tw-text-center">
                      {feature.title}
                    </p>
                    <p className="tw-mt-2 tw-max-w-lg !tw-text-base tw-text-[var(--foreground-color-muted)] default-max-lg:tw-text-center">
                      {feature.description}
                    </p>
                  </div>
                  <div className="tw-flex-grow"></div>
                  <div className="tw-relative tw-mt-4">
                    <div className="tw-relative tw-ml-4 tw-mr-0 tw-overflow-hidden tw-rounded-tl-xl tw-bg-grey_scale-800/50 tw-shadow-2xl tw-outline tw-outline-white/10 sm:tw-ml-10">
                      <div className="tw-relative tw-aspect-video">
                        <img
                          alt={feature.imageAlt}
                          src={feature.imageUrl}
                          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-object-center"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="tw-pointer-events-none tw-absolute tw-inset-px tw-rounded-lg tw-shadow tw-outline tw-outline-black/5" />
              </div>
            ))}
          </div>
        </Section>

        <Section>
          <TitleSection title="Get up and running, fast" description={<>Explore these free resources to help you get started.</>} />
          <div className="tw-mt-16 tw-grid tw-grid-cols-1 tw-gap-8 sm:tw-grid-cols-3">
            {freeResources.map(feature => (
              <div
                className="tw-flex tw-flex-col tw-justify-start tw-gap-4 tw-rounded-2xl tw-bg-grey_scale-800/50 tw-p-8"
                key={`preview-feature-${feature.id}`}
              >
                <div className="tw-h-12 tw-w-12">
                  <Icon icon={feature.icon} className="tw-h-full tw-w-full tw-text-white" />
                </div>
                <h3 className="!tw-text-lg tw-font-semibold tw-tracking-tight tw-text-brand_scale-400">{feature.title}</h3>
                <span className="tw-max-w-[30ch] tw-text-pretty !tw-text-base/6 tw-text-[var(--foreground-color)]">
                  {feature.description}
                </span>
              </div>
            ))}
          </div>
        </Section>

        <ProductFooter />
        <Footer />
      </main>
    </Layout>
  );
};

export default FeaturesSupport;

export const query = graphql`
  query FeaturesSupportQuery {
    site {
      siteMetadata {
        name
        title
        tagline
        description
      }
    }
  }
`;
